import type { ZudokuConfig, ZudokuPlugin } from "zudoku";
import { documentation } from "./sidebar.js";
import { mdxComponents } from "./src/components.js";
import { TutorialSidebarLinks } from "./src/components/getting-started/TutorialSidebarLinks.js";
import { ContentCta } from "./src/components/ContentCta.js";
import { HeadNavigation } from "./src/components/HeadNavigation.js";
import { agenticPlugin } from "./src/agentic/index.js";

// Build-time version registered as an `app_version` super property on the
// PostHog client — matching the Zuplo portal's PostHog naming — so every
// captured event (including the `$exception` events from `capture_exceptions`)
// is tagged with the deploy it came from. The value mirrors the release version
// used by `scripts/upload-sourcemaps.sh` (the Vercel commit SHA, shortened to 7
// chars) so events line up with the `docs@<version>` release the source maps
// are uploaded under. Falls back to "dev" for local builds, where source maps
// aren't uploaded. Computed in Node at build time and baked into the static
// HTML, so the browser never reads `process.env`.
const POSTHOG_RELEASE_VERSION =
  (typeof process !== "undefined" &&
    process.env.VERCEL_GIT_COMMIT_SHA?.slice(0, 7)) ||
  "dev";

// Reference sections that Inkeep should rank slightly lower than guides and
// other articles. Inkeep picks these attributes up on its next crawl; the
// ranking adjustment itself is configured by the Inkeep team per-integration.
const inkeepReferenceSections = ["/programmable-api", "/cli", "/policies"];

const inkeepMetadataPlugin: ZudokuPlugin = {
  getHead: ({ location }) => {
    if (location.pathname.startsWith("/legacy/")) {
      return (
        <>
          <meta name="inkeep:legacy" content="true" />
        </>
      );
    }
    if (
      inkeepReferenceSections.some(
        (section) =>
          location.pathname === section ||
          location.pathname.startsWith(`${section}/`),
      )
    ) {
      return (
        <>
          <meta name="inkeep:section" content="reference" />
        </>
      );
    }
  },
};

// Minimal types for Inkeep search sources; @inkeep/cxkit-types isn't a
// dependency of this repo.
type InkeepSourceTab = string | [string, { breadcrumbs: string[] }];

interface InkeepSource {
  url: string;
  breadcrumbs: string[];
  tabs?: InkeepSourceTab[];
  [key: string]: unknown;
}

const inkeepReferenceUrls = inkeepReferenceSections.map(
  (section) => `zuplo.com/docs${section}`,
);

const getInkeepTab = (url: string): string | null => {
  if (
    inkeepReferenceUrls.some(
      (prefix) => url.includes(`${prefix}/`) || url.endsWith(prefix),
    )
  ) {
    return "Reference";
  }
  if (url.includes("zuplo.com/docs")) {
    return "Docs";
  }
  // Everything else (blog, marketing pages, ...) only shows in the All tab.
  return null;
};

// Categorize each search result into a tab by URL. The tabs themselves are
// declared in search.searchSettings.tabs below.
const transformInkeepSource = (source: InkeepSource): InkeepSource => {
  const tab = getInkeepTab(source.url);
  if (tab === null) {
    return source;
  }
  const existingTabs = source.tabs ?? [];
  const tabExists = existingTabs.some((existingTab) =>
    typeof existingTab === "string"
      ? existingTab === tab
      : existingTab[0] === tab,
  );
  if (tabExists) {
    return source;
  }
  // Hide the leading breadcrumb when it duplicates the tab name.
  const breadcrumbs =
    source.breadcrumbs[0] === tab
      ? source.breadcrumbs.slice(1)
      : source.breadcrumbs;
  return {
    ...source,
    tabs: [...existingTabs, [tab, { breadcrumbs }]],
  };
};

const config: ZudokuConfig = {
  basePath: "/docs",
  canonicalUrlOrigin: "https://zuplo.com",
  cdnUrl: {
    media: "https://cdn.zuplo.com/docs",
  },
  enableStatusPages: true,
  site: {
    title: "Zuplo",
    logo: {
      alt: "Zuplo",
      src: {
        light: "/zuplo-light.svg",
        dark: "/zuplo-dark.svg",
      },
      width: "140px",
      href: "https://zuplo.com",
    },
  },
  metadata: {
    title: "%s - Zuplo Docs",
    description: "Zuplo Documentation",
    generator: "Zudoku",
    favicon: "https://cdn.zuplo.com/www/favicon.svg",
  },
  redirects: [],
  theme: {
    dark: {
      primary: "316 100% 50%",
      border: "#2a2627",
      primaryForeground: "#FFFFFF",
    },
    light: {
      primary: "316 100% 50%",
      border: "220 13.04% 90.98%",
    },
    customCss: `
      .dark svg[id^="mermaid-"] { filter: invert(1); }

      /* Getting Started wizard.

         Zudoku disables the root view-transition group site-wide, so page
         content swaps without a crossfade. The wizard's chrome — the progress
         bar, the method tabs and the step rail — carries its own
         view-transition names, which gives each of them a group of its own that
         the root rule doesn't touch. The result is that the chrome animates
         between wizard pages while every other page on the site keeps the
         instant swap it has today. */
      ::view-transition-group(wizard-progress),
      ::view-transition-group(wizard-methods),
      ::view-transition-group(wizard-steps) {
        animation-duration: 220ms;
        animation-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
      }

      @media (prefers-reduced-motion: reduce) {
        ::view-transition-group(wizard-progress),
        ::view-transition-group(wizard-methods),
        ::view-transition-group(wizard-steps) {
          animation: none;
        }
      }

      /* Each product area's "Getting Started" row is a navigation category
         rather than a doc entry, because only a category's link matches by
         prefix and so stays marked while the reader is inside one of the
         tutorials beneath it (see the gettingStarted helper in sidebar.ts).
         Zudoku styles every category row as a section heading, though, adding
         font-medium whatever the state, which left these rows heavier than the
         plain pages they sit among. They read as links, not headings, so give
         them their siblings' weight and let the active state be the only thing
         that makes a row bold. */
      nav a[href^="/docs/getting-started/"]:not([aria-current="page"]) {
        font-weight: 400;
      }
    `,
  },
  // Setting `languages` replaces Zudoku's default set, so the defaults are
  // re-listed here alongside the additional languages used across the docs.
  syntaxHighlighting: {
    languages: [
      // Zudoku defaults
      "shellscript",
      "javascript",
      "jsx",
      "typescript",
      "tsx",
      "json",
      "jsonc",
      "yaml",
      "python",
      "java",
      "go",
      "graphql",
      "html",
      "css",
      "xml",
      "markdown",
      "mdx",
      // Additional languages used in code samples across the docs
      "c",
      "cpp",
      "csharp",
      "dart",
      "elixir",
      "http",
      "kotlin",
      "lisp",
      "lua",
      "objc",
      "ocaml",
      "php",
      "powershell",
      "ruby",
      "rust",
      "scala",
      "sql",
      "swift",
      "toml",
      "zig",
    ],
  },
  plugins: [
    agenticPlugin(),
    inkeepMetadataPlugin,
    {
      getHead: () => (
        <>
          <script>
            {`!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init be ws Ss me ys capture Ne calculateEventProperties xs register register_once register_for_session unregister unregister_for_session Rs getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Is ks createPersonProfile Ps bs opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing $s debug Es getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
posthog.init('phc_xB1aydh7a41MW9TwUtLJjKme4izQiWf9zKbKhpysAiW', { person_profiles: 'always', ui_host: 'https://us.posthog.com', api_host: 'https://now.zuplo.com', defaults: '2025-05-24', disable_session_recording: true, capture_exceptions: true })
posthog.register({ app_version: '${POSTHOG_RELEASE_VERSION}' })`}
          </script>
          <script>
            {`window.si = window.si || function () { (window.siq = window.siq || []).push(arguments); };`}
          </script>
        </>
      ),
    },
  ],
  slots: {
    "head-navigation-end": HeadNavigation,
    "content-after": ContentCta,
    "content-before": TutorialSidebarLinks,
  },
  search: {
    type: "inkeep",
    apiKey: "499c156cf7a9798343949c8bb5665ac95e48132c6d68c42e",
    integrationId: "clot3asdz0000s601nc8jwnzx",
    organizationId: "org_dDOlt2uJlMWM8oIS",
    primaryBrandColor: "#ff00bd",
    organizationDisplayName: "Zuplo",
    filters: {
      attributes: {
        $and: [
          { legacy: { $ne: "true" } },
          // Exclude marketing "use-cases" pages from docs search. The category
          // comes from the `inkeep:category` meta tag emitted per-page by
          // zuplo.com (see the BaseLayout in the zuplo/www repo). `$ne` keeps
          // every page whose category is not "use-cases" — including docs
          // pages, which carry no `category` attribute at all.
          { category: { $ne: "use-cases" } },
        ],
      },
    },
    transformSource: transformInkeepSource,
    searchSettings: {
      tabs: [
        ["Docs", { isAlwaysVisible: true }],
        "Reference",
        ["All", { isAlwaysVisible: true }],
      ],
    },
  },
  navigation: [
    {
      type: "category",
      label: "Documentation",
      link: {
        type: "doc",
        path: "/",
        file: "home.md",
      },
      items: documentation,
    },
    {
      type: "link",
      label: "API Reference",
      to: "/api",
    },
  ],
  mdx: {
    components: mdxComponents,
  },
  apis: [
    {
      type: "file",
      input: "./api.json",
      path: "api",
      options: {
        expandAllTags: true,
        schemaDownload: {
          enabled: true,
        },
      },
    },
  ],
  docs: {
    files: ["/docs/**/!(*.partial).{md,mdx}"],
    defaultOptions: {
      showLastModified: true,
      suggestEdit: {
        text: "Edit this page",
        url: "https://github.com/zuplo/docs/edit/main/{filePath}",
      },
    },
    publishMarkdown: true,
    llms: {
      instructions: [
        "Many Zuplo docs apply to only part of the product. Each page starts with a **Scope of this page** block under its title that says whether it covers the gateway runtime (API, AI, and MCP gateways) or the Developer Portal, and which hosting options (Managed Edge, Managed Dedicated, Self-Hosted) it holds for, plus any differences by hosting option.",
        "Entries for scoped pages end with the same scope in brackets. Don't apply a page to a hosting option outside its scope. The Developer Portal is a separate site from the gateway, and an Enterprise add-on is a plan, not a hosting option.",
      ].join("\n\n"),
      llmsTxt: true,
      llmsTxtFull: true,
      includeProtected: false,
    },
  },
  sitemap: {
    siteUrl: "https://zuplo.com/docs",
    exclude: ["/", ""],
  },
};

export default config;
